/**
 * Which calendar views a user can open, from their permissions (DynamoDB ivy-astrology-natal-data
 * `permissions.year_YYYY.<prefix>_<view>`; prefix nt = Personal Transit, ntt = Personal Cosmic Activation,
 * tth = Cosmic Personal Events).
 *
 * Annual calendars are sold per calendar year (Claudia, 2026-10-26: "bought for 2027, it shows 2027"). The payment
 * stores <prefix>_annual + <prefix>_annual_start_date (the purchase day) + <prefix>_annual_end_date (31 December of
 * that year) under year_<that year>, and the calendar opens from the week of purchase to the end of that year.
 * Older grants (by hand in the admin panel, or before that change) sit in year_2026 without an end date and keep the
 * original rolling window (their start date, or today, + 365 days).
 */
const YEARS = [2026, 2027, 2028, 2029, 2030];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function getCalendarAccess(permissions, prefix, now = new Date()) {
  const p = permissions || {};
  const y2026 = p.year_2026 || {};
  const flag = (year, view) => (p[`year_${year}`] || {})[`${prefix}_${view}`];
  const dateOf = (year, view, which) => (p[`year_${year}`] || {})[`${prefix}_${view}_${which}_date`];

  // Weekly is free for everyone unless switched off by hand
  const weekly = y2026[`${prefix}_weekly`] !== false;
  const weeklyStartDate = y2026[`${prefix}_weekly_start_date`];

  // Monthly: from whichever year holds it
  const monthlyYear = YEARS.find((year) => flag(year, 'monthly') === true);
  const monthly = monthlyYear !== undefined;
  const monthlyStartDate = monthly ? dateOf(monthlyYear, 'monthly', 'start') : undefined;

  // Annual, sold per calendar year: every purchase that hasn't ended yet
  const today = ymd(now);
  let annualStartDate;
  let annualEndDate;
  for (const year of YEARS) {
    const end = dateOf(year, 'annual', 'end');
    if (flag(year, 'annual') !== true || !end || end < today) continue;
    const start = dateOf(year, 'annual', 'start') || `${year}-01-01`;
    if (!annualStartDate || start < annualStartDate) annualStartDate = start;
    if (!annualEndDate || end > annualEndDate) annualEndDate = end;
  }
  if (annualEndDate) {
    return { weekly, monthly, annual: true, weeklyStartDate, monthlyStartDate, annualStartDate, annualEndDate };
  }

  // Older grants: year_2026 without an end date keep the original rolling window
  const legacyAnnual = y2026[`${prefix}_annual`] === true && !y2026[`${prefix}_annual_end_date`];
  return {
    weekly,
    monthly,
    annual: legacyAnnual,
    weeklyStartDate,
    monthlyStartDate,
    annualStartDate: legacyAnnual ? y2026[`${prefix}_annual_start_date`] : undefined,
    annualEndDate: undefined
  };
}

/** The year an annual calendar bought today is for: from October 1st, next year's calendar is on sale. */
export const annualCalendarYear = (d = new Date()) => (d.getMonth() >= 9 ? d.getFullYear() + 1 : d.getFullYear());

/** End of the given 'YYYY-MM-DD' day in local time (an annual window includes its last day). */
export const endOfDay = (ymdString) => new Date(`${ymdString}T23:59:59.999`);
